import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useModerationStats, useModerationComments } from '@/hooks/useModeration'

function StatCard({ label, count, active, onClick, color }: { label: string, count: number, active: boolean, onClick: () => void, color: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 min-w-[120px] p-4 rounded-xl border text-left transition-all cursor-pointer ${
        active ? `border-${color} bg-${color}/5` : 'border-alvinegro-border bg-white hover:bg-alvinegro-light'
      }`}
    >
      <p className={`text-2xl font-bold ${active ? `text-${color}` : 'text-alvinegro-black'}`}>{count}</p>
      <p className="text-xs text-alvinegro-muted mt-1">{label}</p>
    </button>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    aprovado: 'bg-green-50 text-green-700 border-green-200',
    oculto: 'bg-gray-50 text-gray-700 border-gray-200',
    denunciado: 'bg-red-50 text-red-600 border-red-200',
    pendente: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[status] || colors.pendente}`}>
      {status === 'aprovado' ? 'Aprovado' : status === 'oculto' ? 'Oculto' : status === 'denunciado' ? 'Denunciado' : 'Pendente'}
    </span>
  )
}

export default function ModeracaoPage() {
  const navigate = useNavigate()
  const { user, profile, isLoading: authLoading, isModerator } = useAuth()
  const [statusFilter, setStatusFilter] = useState('todos')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage] = useState(10)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null)

  const { stats, loading: statsLoading } = useModerationStats()
  const { comments, loading, totalCount, refetch, updateStatus, deleteComment, approveAll, deleteAll } = useModerationComments({
    status: statusFilter,
    search: search || undefined,
    page,
    perPage,
  })

  const totalPages = Math.ceil(totalCount / perPage)

  // Show toast helper
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Select all
  const allSelected = comments.length > 0 && selectedIds.length === comments.length
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([])
    } else {
      setSelectedIds(comments.map((c) => c.id))
    }
  }
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  // Bulk actions
  const handleApproveAll = async () => {
    if (selectedIds.length === 0) return
    setActionLoading(true)
    const success = await approveAll(selectedIds)
    if (success) {
      showToast(`${selectedIds.length} comentário(s) aprovado(s)`)
      setSelectedIds([])
    } else {
      showToast('Erro ao aprovar comentários', 'error')
    }
    setActionLoading(false)
  }

  const handleDeleteAll = async () => {
    if (selectedIds.length === 0) return
    setActionLoading(true)
    const success = await deleteAll(selectedIds)
    if (success) {
      showToast(`${selectedIds.length} comentário(s) excluído(s)`)
      setSelectedIds([])
      setDeleteAllConfirm(false)
    } else {
      showToast('Erro ao excluir comentários', 'error')
    }
    setActionLoading(false)
  }

  // Individual actions
  const handleApprove = async (id: string) => {
    setActionLoading(true)
    const success = await updateStatus(id, 'aprovado')
    if (success) {
      showToast('Comentário aprovado')
    } else {
      showToast('Erro ao aprovar', 'error')
    }
    setActionLoading(false)
  }

  const handleHide = async (id: string) => {
    setActionLoading(true)
    const success = await updateStatus(id, 'oculto')
    if (success) {
      showToast('Comentário oculto')
    } else {
      showToast('Erro ao ocultar', 'error')
    }
    setActionLoading(false)
  }

  const handleDelete = async (id: string) => {
    setActionLoading(true)
    const success = await deleteComment(id)
    if (success) {
      showToast('Comentário excluído')
      setDeleteConfirm(null)
    } else {
      showToast('Erro ao excluir', 'error')
    }
    setActionLoading(false)
  }

  const timeAgo = (date: string) => {
    const now = new Date()
    const then = new Date(date)
    const diffMs = now.getTime() - then.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    if (diffMins < 1) return 'Agora'
    if (diffMins < 60) return `${diffMins} min`
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays < 7) return `${diffDays}d`
    return then.toLocaleDateString('pt-BR')
  }

  // Loading auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-alvinegro-light flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-alvinegro-black border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  // Not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-alvinegro-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-alvinegro-light flex items-center justify-center mx-auto mb-4">
            <i className="ri-lock-line text-2xl text-alvinegro-muted"></i>
          </div>
          <h2 className="font-serif font-bold text-xl text-alvinegro-black mb-2">Acesso restrito</h2>
          <p className="text-sm text-alvinegro-muted mb-6">
            Faça login para acessar o painel de moderação de comentários.
          </p>
          <button
            onClick={() => navigate('/noticias')}
            className="bg-alvinegro-black text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            Voltar para notícias
          </button>
        </div>
      </div>
    )
  }

  // Not moderator
  if (!isModerator) {
    return (
      <div className="min-h-screen bg-alvinegro-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-line text-2xl text-red-600"></i>
          </div>
          <h2 className="font-serif font-bold text-xl text-alvinegro-black mb-2">Sem permissão</h2>
          <p className="text-sm text-alvinegro-muted mb-6">
            Você não tem permissão para acessar o painel de moderação. Entre em contato com um administrador.
          </p>
          <button
            onClick={() => navigate('/noticias')}
            className="bg-alvinegro-black text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            Voltar para notícias
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-alvinegro-light">
      {/* Header */}
      <div className="bg-white border-b border-alvinegro-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">Moderação de Comentários</h1>
              <p className="text-sm text-alvinegro-muted mt-1">
                Gerencie comentários aprovados, ocultos e denunciados
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-alvinegro-black text-white text-xs font-medium">
                <i className="ri-shield-check-line text-xs"></i>
                {profile?.role === 'admin' ? 'Administrador' : 'Moderador'}
              </span>
              <button
                onClick={() => refetch()}
                className="w-9 h-9 rounded-full bg-alvinegro-light flex items-center justify-center hover:bg-alvinegro-border transition-colors cursor-pointer"
                title="Atualizar"
              >
                <i className="ri-refresh-line text-sm text-alvinegro-dark"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Stats */}
        <div className="flex flex-wrap gap-3 mb-6">
          <StatCard label="Todos" count={stats.total} active={statusFilter === 'todos'} onClick={() => { setStatusFilter('todos'); setPage(1) }} color="alvinegro-black" />
          <StatCard label="Aprovados" count={stats.aprovados} active={statusFilter === 'aprovado'} onClick={() => { setStatusFilter('aprovado'); setPage(1) }} color="green-600" />
          <StatCard label="Pendentes" count={stats.pendentes} active={statusFilter === 'pendente'} onClick={() => { setStatusFilter('pendente'); setPage(1) }} color="yellow-500" />
          <StatCard label="Ocultos" count={stats.ocultos} active={statusFilter === 'oculto'} onClick={() => { setStatusFilter('oculto'); setPage(1) }} color="gray-500" />
          <StatCard label="Denunciados" count={stats.denunciados} active={statusFilter === 'denunciado'} onClick={() => { setStatusFilter('denunciado'); setPage(1) }} color="red-500" />
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl border border-alvinegro-border p-4 mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
              <div className="relative flex-1 max-w-sm">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-alvinegro-muted"></i>
                <input
                  type="text"
                  placeholder="Buscar comentários..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                  className="w-full pl-9 pr-4 py-2 rounded-full border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                />
              </div>
              {selectedIds.length > 0 && (
                <span className="text-xs text-alvinegro-muted whitespace-nowrap">
                  {selectedIds.length} selecionado(s)
                </span>
              )}
            </div>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleApproveAll}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-check-line text-sm"></i>
                  Aprovar
                </button>
                <button
                  onClick={() => setDeleteAllConfirm(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                  Excluir
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
          {loading || statsLoading ? (
            <div className="p-8 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 animate-pulse">
                  <div className="w-4 h-4 rounded bg-alvinegro-light" />
                  <div className="w-10 h-10 rounded-full bg-alvinegro-light" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-alvinegro-light rounded w-1/4" />
                    <div className="h-3 bg-alvinegro-light rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                      <th className="w-12 px-4 py-3">
                        <button onClick={toggleSelectAll} className="w-5 h-5 rounded border border-alvinegro-border flex items-center justify-center cursor-pointer hover:border-alvinegro-dark transition-colors">
                          {allSelected && <i className="ri-check-line text-xs text-alvinegro-black"></i>}
                        </button>
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Usuário</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Comentário</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">Notícia</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Data</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comments.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-alvinegro-muted">
                          <i className="ri-chat-3-line text-4xl mb-3 block"></i>
                          <p className="text-sm">Nenhum comentário encontrado</p>
                        </td>
                      </tr>
                    ) : (
                      comments.map((comment) => (
                        <tr key={comment.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleSelect(comment.id)}
                              className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer transition-colors ${
                                selectedIds.includes(comment.id)
                                  ? 'bg-alvinegro-black border-alvinegro-black'
                                  : 'border-alvinegro-border hover:border-alvinegro-dark'
                              }`}
                            >
                              {selectedIds.includes(comment.id) && <i className="ri-check-line text-xs text-white"></i>}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-alvinegro-black flex items-center justify-center overflow-hidden shrink-0">
                                {comment.user_avatar ? (
                                  <img src={comment.user_avatar} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <span className="text-white font-bold text-xs">{comment.user_name.substring(0, 2).toUpperCase()}</span>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-alvinegro-black truncate">{comment.user_name}</p>
                                <p className="text-xs text-alvinegro-muted truncate">{comment.user_id.substring(0, 8)}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-alvinegro-dark max-w-[300px] truncate">{comment.content}</p>
                            {comment.parent_id && (
                              <span className="text-xs text-alvinegro-muted mt-1 inline-block">Resposta</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <span className="text-xs text-alvinegro-muted truncate block max-w-[120px]">{comment.news_id.substring(0, 8)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={comment.status} />
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <span className="text-xs text-alvinegro-muted">{timeAgo(comment.created_at)}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {comment.status !== 'aprovado' && (
                                <button
                                  onClick={() => handleApprove(comment.id)}
                                  disabled={actionLoading}
                                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                                  title="Aprovar"
                                >
                                  <i className="ri-check-line text-sm"></i>
                                </button>
                              )}
                              {comment.status !== 'oculto' && (
                                <button
                                  onClick={() => handleHide(comment.id)}
                                  disabled={actionLoading}
                                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 text-gray-600 transition-colors cursor-pointer"
                                  title="Ocultar"
                                >
                                  <i className="ri-eye-off-line text-sm"></i>
                                </button>
                              )}
                              <button
                                onClick={() => setDeleteConfirm(comment.id)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                                title="Excluir"
                              >
                                <i className="ri-delete-bin-line text-sm"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-alvinegro-border">
                  <p className="text-xs text-alvinegro-muted">
                    Mostrando {Math.min((page - 1) * perPage + 1, totalCount)}-{Math.min(page * perPage, totalCount)} de {totalCount}
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-left-s-line text-sm"></i>
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + Math.max(1, Math.min(page - 2, totalPages - 4))
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                            pageNum === page
                              ? 'bg-alvinegro-black text-white'
                              : 'hover:bg-alvinegro-light text-alvinegro-dark'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Excluir comentário</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-6">
              Tem certeza que deseja excluir este comentário permanentemente? Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {actionLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Confirm Modal */}
      {deleteAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Excluir múltiplos</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-6">
              Tem certeza que deseja excluir {selectedIds.length} comentário(s) permanentemente? Esta ação não pode ser desfeita.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDeleteAllConfirm(false)}
                className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={actionLoading}
                className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50"
              >
                {actionLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-white border-green-200 text-green-700'
              : 'bg-white border-red-200 text-red-600'
          }`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}